<template>
  <main class="hc-compact-page billing-page">
    <!-- Header Card -->
        <PageHeader icon="fas fa-file-invoice-dollar">
      <template #title>
        Daftar Langganan {{ clinic_name }}
      </template>
      <template #subtitle>
        Pantau invoice dan status pembayaran klinik dalam satu tampilan
      </template>
      <template #actions>
        <b-button
                class="btn btn-primary font-weight-bold px-6 hc-btn-tambah-jadwal"
                @click="$router.push('/subscribe/package')">
                <i class="fas fa-plus"></i>
                <span>Pilih Paket</span>
              </b-button>
      </template>
    </PageHeader>

    <section class="billing-stat-grid" aria-label="Ringkasan langganan pada halaman ini">
      <article class="billing-stat-card">
        <span class="billing-stat-card__icon"><i class="fas fa-receipt"></i></span>
        <div>
          <small>Invoice ditampilkan</small>
          <strong>{{ items.length }}</strong>
          <span>Pada halaman ini</span>
        </div>
      </article>
      <article class="billing-stat-card billing-stat-card--paid">
        <span class="billing-stat-card__icon"><i class="fas fa-check-circle"></i></span>
        <div>
          <small>Sudah lunas</small>
          <strong>{{ paidCount }}</strong>
          <span>Pembayaran selesai</span>
        </div>
      </article>
      <article class="billing-stat-card billing-stat-card--pending">
        <span class="billing-stat-card__icon"><i class="fas fa-clock"></i></span>
        <div>
          <small>Perlu tindakan</small>
          <strong>{{ pendingCount }}</strong>
          <span>Belum lunas dan aktif</span>
        </div>
      </article>
    </section>

    <div class="row">
      <div class="col-md-12">
        <Card card-class="billing-table-shell">
          <template v-slot:body>
            <div class="billing-table-heading">
              <div>
                <span class="billing-eyebrow">Riwayat transaksi</span>
                <h2>Invoice langganan</h2>
                <p>Daftar tagihan, status pembayaran, dan masa aktif paket.</p>
              </div>
              <span class="billing-table-heading__count">{{ totalRows }} invoice</span>
            </div>

            <b-table
              striped
              hover
              responsive
              class="billing-table"
              :items="items"
              :fields="fields"
            >
              <template #table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.key === 'actions' ? '20%' : '' }"
                />
              </template>
              <template #cell(payment_status)="data">
                <b-badge
                  pill
                  variant="success"
                  v-if="
                    data.item.workstate_name == 'Paid' &&
                    data.item.is_expired == 0
                  "
                  >Lunas</b-badge
                >
                <b-badge
                  pill
                  variant="warning"
                  v-if="
                    data.item.workstate_name == 'Unpaid' &&
                    data.item.is_expired == 0
                  "
                  >Belum Lunas</b-badge
                >
                <b-badge pill variant="danger" v-if="data.item.is_expired == 1"
                  >Kadaluarsa</b-badge
                >
              </template>
              <template #cell(display_total)="data">
                <strong class="billing-amount">
                  Rp {{ parseInt(data.item.display_total).toLocaleString("id-ID") }}
                </strong>
              </template>
              <template #cell(actions)="data">
                <div class="billing-actions">
                  <b-button
                  size="sm"
                  class="billing-action billing-action--detail"
                  v-b-tooltip.hover
                  title="Detail"
                  aria-label="Lihat detail invoice"
                  @click="
                    $router.push({ path: '/billings/detail/' + data.item.id })
                  "
                >
                  <i class="fas fa-eye"></i>
                </b-button>
                <b-button
                  size="sm"
                  class="billing-action billing-action--pay"
                  v-b-tooltip.hover
                  title="Bayar"
                  aria-label="Bayar invoice"
                  v-if="
                    data.item.workstate_name == 'Unpaid' &&
                    data.item.is_expired == 0
                  "
                  @click="setPaymentData(data.item)"
                >
                  <i class="fas fa-credit-card"></i>
                </b-button>
                <b-button
                  size="sm"
                  class="billing-action billing-action--invoice"
                  v-b-tooltip.hover
                  title="Invoice"
                  aria-label="Buka invoice"
                  v-if="data.item.workstate_name == 'Paid'"
                  @click="
                    $router.push({ path: '/billings/invoice/' + data.item.id })
                  "
                >
                  <i class="fas fa-file-invoice"></i>
                </b-button>
                <b-button
                  v-if="data.item.is_expired == 1"
                  size="sm"
                  class="billing-action billing-action--delete"
                  v-b-tooltip.hover
                  title="Hapus"
                  aria-label="Hapus invoice kadaluarsa"
                  @click="deleteBilling(data.item.id)"
                >
                  <i class="fas fa-trash-alt"></i>
                </b-button>
                </div>
              </template>
            </b-table>
            <b-pagination
              v-if="items.length != 0"
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              @page-click="pageOnClick"
              class="mt-4"
            ></b-pagination>
          </template>
        </Card>
      </div>
    </div>
  </main>
</template>

<script>
import Card from "@/view/content/Card.vue";
import saasModule from "@/core/modules/CrudSaasModule.js";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

export default {
  components: {
    Card,
  },

  data() {
    return {
      // Filter
      filter: {
        start_date: "",
        end_date: "",
      },
      // Pagination
      perPage: 20,
      currentPage: 1,
      totalRows: 0,
      fields: [
        {
          key: "number",
          label: "#",
          sortable: true,
        },
        {
          key: "display_invoice_id",
          label: "No. Invoice",
          sortable: true,
        },
        {
          key: "tenant_name",
          label: "Nama Klinik",
          sortable: true,
        },
        {
          key: "subscription_package_name",
          label: "Paket",
          sortable: true,
        },
        {
          key: "subscription_package_duration",
          label: "Jumlah Hari",
          sortable: true,
        },
        {
          key: "display_total",
          label: "Total Pembayaran",
          sortable: true,
        },
        {
          key: "payment_status",
          label: "Status Pembayaran",
          sortable: true,
        },

        { key: "actions", label: "Aksi" },
      ],
      items: [],
      blood_type: [],
      // User Access
      btn: false,
      clinic_name:
        window.configuration == null
          ? "Medhop"
          : window.configuration.clinic_name,
    };
  },

  computed: {
    paidCount() {
      return this.items.filter(
        (item) => item.workstate_name === "Paid" && item.is_expired == 0
      ).length;
    },

    pendingCount() {
      return this.items.filter(
        (item) => item.workstate_name === "Unpaid" && item.is_expired == 0
      ).length;
    },
  },

  methods: {
    pageOnClick(page) {
      this.currentPage = page;
      this.pagination();
    },

    async pagination() {
      let tenant = JSON.parse(localStorage.getItem("tenantconfig"));
      let filterParams = `&start_date=${this.filter.start_date}&end_date=${this.filter.end_date}`;
      let response = await saasModule.paginate(
        `billings/tenant/${tenant.id}`,
        `?page=${this.currentPage}&page_size=${this.perPage}${filterParams}`
      );
      let pagination = response.meta.pagination;
      this.totalRows = pagination.total;

      let a, b;
      b = this.perPage * (this.currentPage - 1) + 1;
      for (a = 0; a < response.data.length; a++) {
        response.data[a].number = b + a;
      }
      this.items = response.data;
    },

    setPaymentData(val) {
      let duration = val.subscription_package_duration / 30;

      let data = {
        duration: duration,
        id: val.id,
        name: val.subscription_package_name,
        payment_method_category: val.payment_bank,
        payment_method_code: val.code,
        payment_method_name: val.payment_bank,
        price: val.subscription_package_price,
        tax_amount: val.tax_amount,
        total_amount: val.display_total,
      };

      localStorage.setItem("paymentDetailData", JSON.stringify(data));
      this.$router.push({ path: "/subscribe/payment/" });
    },

    async deleteBilling(id) {
      // Delete Data
      let result = await saasModule.delete("billings/" + id);
      // If Deleted
      if (result) {
        this.pagination();
      }
    },

    // async getBloodTypeOption() {
    //   let response = await module.setTreeSelect("blood-types");
    //   // Success
    //   if (response.state == "success") {
    //     // Set Option Select
    //     this.blood_type = response.data;
    //     this.blood_type.unshift({
    //       label: "Filter Berdasar Gol. Darah",
    //       id: "",
    //       isDisabled: true,
    //     });
    //   }
    // },

    // filterByName() {
    //   // this.pagination();
    //   this.generalFilter()
    // },

    // filterByBloodType(evt) {
    //   this.filter.blood_type_id = evt.id
    //   this.generalFilter()
    // },

    generalFilter() {
      this.pagination();
    },

    // setActiveButton(){
    //   let access_right_user = window.localStorage.getItem("access_right_display")
    //   let access_right = JSON.parse(access_right_user)

    //   let a
    //   for(a = 0; a < access_right.length; a++){
    //     if(access_right[a] == "2202"){
    //       this.btn = true
    //     }
    //   }
    // },
  },

  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Daftar Langganan", route: "/billings" },
    ]);

    this.pagination();
    // this.getBloodTypeOption();
    // this.setActiveButton()
  },

  // watch: {
  //   'filter.blood_type_id': function (newVal, oldVal) {
  //     if (typeof newVal === 'undefined') {
  //       this.filter.blood_type_id = ''
  //       this.generalFilter()
  //     }
  //   }
  // },
};
</script>

<style lang="scss">
.billing-page {
  --billing-navy: #143263;
  --billing-blue: #14608f;
  --billing-teal: #1e958f;
  --billing-border: #e3eaf2;
}

.billing-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
  padding: 17px 19px;
  border: 1px solid var(--billing-border);
  border-radius: 17px;
  background: linear-gradient(110deg, rgba(20, 50, 99, 0.08), rgba(30, 149, 143, 0.07));
  box-shadow: 0 9px 24px rgba(20, 50, 99, 0.055);
}

.billing-hero__content {
  display: flex;
  align-items: center;
  gap: 13px;
}

.billing-hero__icon {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  place-items: center;
  color: #fff;
  border-radius: 13px;
  background: linear-gradient(135deg, var(--billing-navy), var(--billing-blue), var(--billing-teal));
  box-shadow: 0 7px 16px rgba(20, 96, 143, 0.18);
  font-size: 17px;
}

.billing-eyebrow {
  display: block;
  margin-bottom: 3px;
  color: var(--billing-teal);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.billing-hero h1 {
  margin: 0 0 3px;
  color: #1a2433 !important;
  font-size: 21px !important;
  font-weight: 700;
}

.billing-hero p,
.billing-table-heading p {
  margin: 0;
  color: #6b7280;
  font-size: 11px;
}

.billing-subscribe-button {
  display: inline-flex;
  min-height: 38px;
  padding: 8px 14px;
  align-items: center;
  gap: 8px;
  color: #fff;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--billing-navy), var(--billing-blue), var(--billing-teal));
  box-shadow: 0 7px 16px rgba(20, 96, 143, 0.18);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.billing-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.billing-stat-card {
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 76px;
  padding: 11px 13px;
  overflow: hidden;
  border: 1px solid var(--billing-border);
  border-radius: 15px;
  background: #fff;
  box-shadow: 0 7px 20px rgba(20, 50, 99, 0.045);
}

.billing-stat-card__icon {
  display: inline-flex !important;
  width: 38px !important;
  min-width: 38px !important;
  height: 38px !important;
  min-height: 38px !important;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  color: var(--billing-blue);
  border-radius: 11px;
  background: rgba(20, 96, 143, 0.09);
}

.billing-stat-card__icon i {
  display: block;
  width: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  color: inherit !important;
  font-size: 14px;
  line-height: 1;
}

.billing-stat-card--paid .billing-stat-card__icon {
  color: var(--billing-teal);
  background: rgba(30, 149, 143, 0.1);
}

.billing-stat-card--pending .billing-stat-card__icon {
  color: #b66c07;
  background: rgba(245, 158, 11, 0.11);
}

.billing-stat-card > div > small,
.billing-stat-card > div > strong,
.billing-stat-card > div > span {
  display: block;
}

.billing-stat-card > div > small {
  color: #748196 !important;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
}

.billing-stat-card > div > strong {
  margin: 1px 0;
  color: var(--billing-navy);
  font-size: 18px;
  font-weight: 700;
}

.billing-stat-card > div > span {
  color: #8a96a8;
  font-size: 9px;
}

.billing-page .billing-table-shell.card {
  border-radius: 17px !important;
  transform: none !important;
}

.billing-page .billing-table-shell > .card-body {
  padding: 16px !important;
}

.billing-table-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.billing-table-heading h2 {
  margin: 0 0 3px;
  color: #1a2433 !important;
  font-size: 16px !important;
  font-weight: 700;
}

.billing-table-heading__count {
  padding: 6px 10px;
  color: var(--billing-blue);
  border: 1px solid rgba(20, 96, 143, 0.14);
  border-radius: 999px;
  background: rgba(20, 96, 143, 0.07);
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
}

.billing-page .billing-table .table {
  margin-bottom: 0;
}

.billing-page .billing-table.table-responsive {
  margin: 0;
  border: 1px solid var(--billing-border);
  border-radius: 12px;
}

.billing-amount {
  color: var(--billing-navy);
  font-size: 11px;
  white-space: nowrap;
}

.billing-actions {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.billing-page .billing-action.btn {
  display: inline-grid !important;
  width: 31px !important;
  min-width: 31px !important;
  height: 31px !important;
  min-height: 31px !important;
  padding: 0 !important;
  place-items: center;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.billing-page .billing-action i {
  margin: 0 !important;
  padding: 0 !important;
  color: inherit !important;
  font-size: 10px;
}

.billing-action--detail {
  color: var(--billing-blue) !important;
  border: 1px solid rgba(20, 96, 143, 0.2) !important;
  background: rgba(20, 96, 143, 0.08) !important;
}

.billing-action--pay {
  color: #a9680e !important;
  border: 1px solid rgba(245, 158, 11, 0.23) !important;
  background: rgba(245, 158, 11, 0.1) !important;
}

.billing-action--invoice {
  color: var(--billing-teal) !important;
  border: 1px solid rgba(30, 149, 143, 0.22) !important;
  background: rgba(30, 149, 143, 0.09) !important;
}

.billing-action--delete {
  color: #d14343 !important;
  border: 1px solid rgba(239, 68, 68, 0.2) !important;
  background: rgba(239, 68, 68, 0.08) !important;
}

@media (max-width: 767.98px) {
  .billing-hero {
    align-items: flex-start;
    flex-direction: column;
  }

  .billing-stat-grid {
    grid-template-columns: 1fr;
  }

  .billing-stat-card {
    min-height: 70px;
  }
}
</style>
